import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense, useEffect, useRef } from "react";
import { useAuth } from "./_core/hooks/useAuth";
import { useSessionExpiry } from "./hooks/useSessionExpiry";
import AdminReAuthModal from "./components/AdminReAuthModal";
import { Route, Switch, useLocation } from "wouter";
import GlobalBreadcrumbs from "@/components/GlobalBreadcrumbs";
import { trackPageView } from "@/lib/analytics";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import CookieConsent from "./components/CookieConsent";
import Redirects from "./components/Redirects";
import SalesAssistantLoader from "./components/sales-assistant/SalesAssistantLoader";
import MobileScoreCta from "./components/MobileScoreCta";
import { ScoreBookingCompletionListener } from "./components/ScoreBookingLink";
import { prepareRuntimeSchemaRoute } from "./components/SchemaScript";

// --- Eagerly loaded (always needed on first paint) ---
import Home from "./pages/Home";
import NotFound from "@/pages/NotFound";

// --- Lazily loaded route chunks ---
const Blog = lazy(() => import("@/pages/Blog"));
const BlogSearchResults = lazy(() => import("@/pages/BlogSearchResults"));
const AIMarketingNewsHub = lazy(() => import("@/pages/AIMarketingNewsHub"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const About = lazy(() => import("./pages/About"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const EditorialPolicy = lazy(() => import("./pages/EditorialPolicy"));
const Methodology = lazy(() => import("./pages/Methodology"));
const ProviderScopeCard = lazy(() => import("./pages/ProviderScopeCard"));
const ChatGPTAnswersGuide = lazy(() => import("./pages/ChatGPTAnswersGuide"));
const NewCatParentEssentialsGuide = lazy(() => import("./pages/NewCatParentEssentialsGuide"));
const ModiElnadi = lazy(() => import("./pages/ModiElnadi"));

// Case studies
const CaseStudy = lazy(() => import("./pages/CaseStudy"));
const CaseStudySaaS = lazy(() => import("./pages/CaseStudySaaS"));
const CaseStudiesHub = lazy(() => import("./pages/CaseStudiesHub"));
const CaseStudyKenya = lazy(() => import("./pages/CaseStudyKenya"));
const CaseStudyAgentic = lazy(() => import("./pages/CaseStudyAgentic"));
// Hubs & resources
const AEOHub = lazy(() => import("./pages/AEOHub"));
const GoogleAdsOptimisationGuide = lazy(() => import("./pages/GoogleAdsOptimisationGuide"));
const B2BLeadGenerationGuide = lazy(() => import("./pages/B2BLeadGenerationGuide"));
const AISearchPpcAgenciesGuide = lazy(() => import("./pages/AISearchPpcAgenciesGuide"));
const B2BMarketingAgenciesGuide = lazy(() => import("./pages/B2BMarketingAgenciesGuide"));
const AIToolsHub = lazy(() => import("./pages/AIToolsHub"));
const ToolsIndex = lazy(() => import("./pages/ToolsIndex"));
const UTMBuilder = lazy(() => import("./pages/UTMBuilder"));
const AIPromptImprover = lazy(() => import("./pages/AIPromptImprover"));
const AITokenCalculator = lazy(() => import("./pages/AITokenCalculator"));
const AIEconomicsScorecard = lazy(() => import("./pages/AIEconomicsScorecard"));
const AuditPage = lazy(() => import("./pages/AuditPage"));
const FreeAuditThankYouPage = lazy(() => import("./pages/FreeAuditThankYouPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const StartPage = lazy(() => import("./pages/StartPage"));
const Contact = lazy(() => import("./pages/Contact"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const AdminContacts = lazy(() => import("./pages/AdminContacts"));
const AdminBrokenLinks = lazy(() => import("./pages/AdminBrokenLinks"));
const PageSpeedDashboard = lazy(() => import("./pages/PageSpeedDashboard"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminLinkArtemisImports = lazy(() => import("./pages/AdminLinkArtemisImports"));
const AdminNewsletterSubscribers = lazy(() => import("./pages/AdminNewsletterSubscribers"));
const Unsubscribed = lazy(() => import("./pages/Unsubscribed"));
const ArabicPilotPage = lazy(() => import("./pages/ArabicPilotPage"));

// Service pages
const ServiceAgenticAI = lazy(() => import("./pages/ServiceAgenticAI"));
const ServiceSEO = lazy(() => import("./pages/ServiceSEO"));
const ServiceAICitation = lazy(() => import("./pages/ServiceAICitation"));
const ServiceAICitationProgramme = lazy(() => import("./pages/ServiceAICitationProgramme"));
const ServiceAIMarketingEcommerce = lazy(() => import("./pages/ServiceAIMarketingEcommerce"));
const ServicePPC = lazy(() => import("./pages/ServicePPC"));
const ServiceStrategy = lazy(() => import("./pages/ServiceStrategy"));
const ServiceABM = lazy(() => import("./pages/ServiceABM"));
const ServiceAIMode = lazy(() => import("./pages/ServiceAIMode"));
const ServiceAIGovernance = lazy(() => import("./pages/ServiceAIGovernance"));
const ServiceGoogleAIMode = lazy(() => import("./pages/ServiceGoogleAIMode"));
const ServicePreferredSources = lazy(() => import("./pages/ServicePreferredSources"));
const ServiceAITraining = lazy(() => import("./pages/ServiceAITraining"));
const ServicePerformanceMax = lazy(() => import("./pages/ServicePerformanceMax"));
const ServiceAIWebsites = lazy(() => import("./pages/ServiceAIWebsites"));
const ServiceIMC = lazy(() => import("./pages/ServiceIMC"));
const ServiceAEOLondon = lazy(() => import("./pages/ServiceAEOLondon"));
const ServiceGEOLondon = lazy(() => import("./pages/ServiceGEOLondon"));
const ServiceAIMarketingLondon = lazy(() => import("./pages/ServiceAIMarketingLondon"));
const LocationLondon = lazy(() => import("./pages/LocationLondon"));
const ServiceLocalPresence = lazy(() => import("./pages/ServiceLocalPresence"));
const ServiceAIVisibilityPlan = lazy(() => import("./pages/ServiceAIVisibilityPlan"));
const ServiceFullAIGrowthEngine = lazy(() => import("./pages/ServiceFullAIGrowthEngine"));
const ServicesHub = lazy(() => import("./pages/ServicesHub"));
const FAQPage = lazy(() => import("./pages/FAQPage"));

// Registry-backed local business hub and 28 industry/area guides.
const LocalLandingPage = lazy(() => import("./components/LocalLandingPage"));

// Minimal fallback shown while a lazy chunk loads
function PageLoader() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0F1419",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          border: "3px solid #FF8C00",
          borderTopColor: "transparent",
          borderRadius: "50%",
          animation: "spin 0.7s linear infinite",
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function Router() {
  // P2: SPA route change tracking - fires a GA4 page_view on every navigation.
  // IMPORTANT: Skip the FIRST render (initial page load) because GTM's GA4
  // Configuration tag already fires a page_view on load. Firing a second one
  // here would create a duplicate hit. We only fire for subsequent SPA
  // navigations where GTM does NOT automatically fire a new page_view.
  const [location] = useLocation();
  const isFirstRender = useRef(true);
  useEffect(() => {
    // Keep the client-side JSON-LD graph route-scoped. Prerendered pages supply
    // their graph in <head>; subsequent SPA routes rebuild it from declarations.
    prepareRuntimeSchemaRoute();
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // GTM handles the initial page_view - skip here
    }
    // Small delay so the new page's <title> has time to update via SEOHead
    const timer = setTimeout(() => {
      trackPageView(location, document.title);
    }, 150);
    return () => clearTimeout(timer);
  }, [location]);

  // make sure to consider if you need authentication for certain routes
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/ar" component={ArabicPilotPage} />
        <Route path="/ar/" component={ArabicPilotPage} />
        <Route path="/ar/blog/:slug" component={ArabicPilotPage} />
        <Route path="/ar/blog/:slug/" component={ArabicPilotPage} />
        <Route path="/ar/services/:slug" component={ArabicPilotPage} />
        <Route path="/ar/services/:slug/" component={ArabicPilotPage} />
        <Route path="/ar/:rest*" component={ArabicPilotPage} />
        <Route path={"/"} component={Home} />
        <Route path="/blog" component={Blog} />
        <Route path="/blog/" component={Blog} />
        <Route path="/blog/search" component={BlogSearchResults} />
        <Route path="/blog/search/" component={BlogSearchResults} />
        <Route path="/blog/topics" component={Blog} />
        <Route path="/blog/topics/" component={Blog} />
        <Route path="/blog/topics/:topic" component={Blog} />
        <Route path="/blog/topics/:topic/" component={Blog} />
        <Route path="/ai-marketing-news" component={AIMarketingNewsHub} />
        <Route path="/ai-marketing-news/" component={AIMarketingNewsHub} />
        <Route path="/pricing" component={PricingPage} />
        <Route path="/pricing/" component={PricingPage} />
        <Route path="/start" component={StartPage} />
        <Route path="/start/" component={StartPage} />
        <Route path={"/blog/:slug"} component={BlogPost} />
        <Route path={"/blog/:slug/"} component={BlogPost} />
        <Route path={"/about"} component={About} />
        <Route path={"/about/"} component={About} />
        <Route path={"/contact"} component={Contact} />
        <Route path={"/contact/"} component={Contact} />
        <Route path={"/privacy-policy"} component={PrivacyPolicy} />
        <Route path={"/privacy-policy/"} component={PrivacyPolicy} />
        <Route path={"/cookie-policy"} component={CookiePolicy} />
        <Route path={"/cookie-policy/"} component={CookiePolicy} />
        <Route path={"/editorial-policy"} component={EditorialPolicy} />
        <Route path={"/editorial-policy/"} component={EditorialPolicy} />
        <Route path={"/methodology"} component={Methodology} />
        <Route path={"/methodology/"} component={Methodology} />
        <Route path={"/provider-scope-card"} component={ProviderScopeCard} />
        <Route path={"/provider-scope-card/"} component={ProviderScopeCard} />
        <Route path={"/how-b2b-brands-appear-in-chatgpt-answers"} component={ChatGPTAnswersGuide} />
        <Route path={"/how-b2b-brands-appear-in-chatgpt-answers/"} component={ChatGPTAnswersGuide} />
        <Route path={"/new-cat-parent-essentials"} component={NewCatParentEssentialsGuide} />
        <Route path={"/new-cat-parent-essentials/"} component={NewCatParentEssentialsGuide} />
        <Route path={"/case-studies"} component={CaseStudiesHub} />
        <Route path={"/case-studies/"} component={CaseStudiesHub} />
        <Route path={"/case-studies/b2b-saas-abm"} component={CaseStudySaaS} />
        <Route path={"/case-studies/b2b-saas-abm/"} component={CaseStudySaaS} />
        <Route path={"/case-studies/destination-marketing"} component={CaseStudyKenya} />
        <Route path={"/case-studies/destination-marketing/"} component={CaseStudyKenya} />
        <Route path={"/case-studies/agentic-ai-gtm"} component={CaseStudyAgentic} />
        <Route path={"/case-studies/agentic-ai-gtm/"} component={CaseStudyAgentic} />
        {/* Legacy: old single case study URL now redirects to hub */}
        <Route path={"/case-studies/fintech-persona-targeting"} component={CaseStudy} />
        <Route path={"/case-studies/fintech-persona-targeting/"} component={CaseStudy} />
        <Route path={"/faq"} component={FAQPage} />
        <Route path={"/faq/"} component={FAQPage} />
        <Route path={"/aeo"} component={AEOHub} />
        <Route path={"/aeo/"} component={AEOHub} />
        <Route path={"/google-ads-optimization-ppc-performance"} component={GoogleAdsOptimisationGuide} />
        <Route path={"/google-ads-optimization-ppc-performance/"} component={GoogleAdsOptimisationGuide} />
        <Route path={"/b2b-lead-generation-strategies-best"} component={B2BLeadGenerationGuide} />
        <Route path={"/b2b-lead-generation-strategies-best/"} component={B2BLeadGenerationGuide} />
        <Route path={"/ai-search-ppc-agencies-london-comparison"} component={AISearchPpcAgenciesGuide} />
        <Route path={"/ai-search-ppc-agencies-london-comparison/"} component={AISearchPpcAgenciesGuide} />
        <Route path={"/b2b-marketing-agencies-london-pricing"} component={B2BMarketingAgenciesGuide} />
        <Route path={"/b2b-marketing-agencies-london-pricing/"} component={B2BMarketingAgenciesGuide} />
        <Route path={"/resources/ai-tools"} component={AIToolsHub} />
        <Route path={"/resources/ai-tools/"} component={AIToolsHub} />
        <Route path={"/tools"} component={ToolsIndex} />
        <Route path={"/tools/"} component={ToolsIndex} />
        <Route path={"/tools/utm-builder"} component={UTMBuilder} />
        <Route path={"/tools/utm-builder/"} component={UTMBuilder} />
        <Route path={"/tools/ai-prompt-improver"} component={AIPromptImprover} />
        <Route path={"/tools/ai-prompt-improver/"} component={AIPromptImprover} />
        <Route path={"/tools/ai-token-calculator"} component={AITokenCalculator} />
        <Route path={"/tools/ai-token-calculator/"} component={AITokenCalculator} />
        <Route path={"/tools/ai-economics-scorecard"} component={AIEconomicsScorecard} />
        <Route path={"/tools/ai-economics-scorecard/"} component={AIEconomicsScorecard} />
        {/* Services Hub */}
        <Route path={"/services"} component={ServicesHub} />
        <Route path={"/services/"} component={ServicesHub} />
        {/* Service Landing Pages */}
        <Route path={"/services/gemini-agentic-ai"} component={ServiceAgenticAI} />
        <Route path={"/services/gemini-agentic-ai/"} component={ServiceAgenticAI} />
        <Route path={"/services/seo-aeo-geo"} component={ServiceSEO} />
        <Route path={"/services/seo-aeo-geo/"} component={ServiceSEO} />
        <Route path={"/services/ai-citation-service"} component={ServiceAICitation} />
        <Route path={"/services/ai-citation-service/"} component={ServiceAICitation} />
      <Route path={"/services/ai-citation-programme"} component={ServiceAICitationProgramme} />
      <Route path={"/services/ai-citation-programme/"} component={ServiceAICitationProgramme} />
      <Route path={"/services/ai-marketing-agency-for-ecommerce"} component={ServiceAIMarketingEcommerce} />
      <Route path={"/services/ai-marketing-agency-for-ecommerce/"} component={ServiceAIMarketingEcommerce} />
        <Route path={"/services/ppc-performance-max"} component={ServicePPC} />
        <Route path={"/services/ppc-performance-max/"} component={ServicePPC} />
        <Route path={"/services/ai-marketing-strategy"} component={ServiceStrategy} />
        <Route path={"/services/ai-marketing-strategy/"} component={ServiceStrategy} />
        <Route path={"/services/account-based-marketing"} component={ServiceABM} />
        <Route path={"/services/account-based-marketing/"} component={ServiceABM} />
        <Route path={"/services/google-ai-mode-optimisation"} component={ServiceAIMode} />
        <Route path={"/services/google-ai-mode-optimisation/"} component={ServiceAIMode} />
        <Route path={"/services/ai-governance"} component={ServiceAIGovernance} />
        <Route path={"/services/ai-governance/"} component={ServiceAIGovernance} />
        <Route path={"/services/preferred-sources-strategy"} component={ServicePreferredSources} />
        <Route path={"/services/preferred-sources-strategy/"} component={ServicePreferredSources} />
        <Route path={"/services/ai-training-for-leaders"} component={ServiceAITraining} />
        <Route path={"/services/ai-training-for-leaders/"} component={ServiceAITraining} />
        <Route path={"/services/performance-max-agency"} component={ServicePerformanceMax} />
        <Route path={"/services/performance-max-agency/"} component={ServicePerformanceMax} />
        <Route path={"/services/ai-websites"} component={ServiceAIWebsites} />
        <Route path={"/services/ai-websites/"} component={ServiceAIWebsites} />
        <Route path={"/services/integrated-marketing-communications"} component={ServiceIMC} />
        <Route path={"/services/integrated-marketing-communications/"} component={ServiceIMC} />
        <Route path={"/services/aeo-agency-london"} component={ServiceAEOLondon} />
        <Route path={"/services/aeo-agency-london/"} component={ServiceAEOLondon} />
        <Route path={"/services/geo-agency-london"} component={ServiceGEOLondon} />
        <Route path={"/services/geo-agency-london/"} component={ServiceGEOLondon} />
        <Route path={"/services/ai-marketing-agency-london"} component={ServiceAIMarketingLondon} />
        <Route path={"/services/ai-marketing-agency-london/"} component={ServiceAIMarketingLondon} />
        <Route path={"/services/local-presence-plan"} component={ServiceLocalPresence} />
        <Route path={"/services/local-presence-plan/"} component={ServiceLocalPresence} />
        <Route path={"/services/ai-visibility-plan"} component={ServiceAIVisibilityPlan} />
        <Route path={"/services/ai-visibility-plan/"} component={ServiceAIVisibilityPlan} />
        <Route path={"/services/full-ai-growth-engine"} component={ServiceFullAIGrowthEngine} />
        <Route path={"/services/full-ai-growth-engine/"} component={ServiceFullAIGrowthEngine} />
        {/* Source-derived local business hub and industry/area guides. */}
        <Route path={"/local"} component={LocalLandingPage} />
        <Route path={"/local/"} component={LocalLandingPage} />
        <Route path={"/local/:slug"} component={LocalLandingPage} />
        <Route path={"/local/:slug/"} component={LocalLandingPage} />
        <Route path={"/ai-marketing-agency-london"} component={LocationLondon} />
        <Route path={"/ai-marketing-agency-london/"} component={LocationLondon} />
        <Route path={"/ai-website-agency-london"} component={LocationLondon} />
        <Route path={"/ai-website-agency-london/"} component={LocationLondon} />
        <Route path={"/marketing-agency-wimbledon"} component={LocationLondon} />
        <Route path={"/marketing-agency-wimbledon/"} component={LocationLondon} />
        {/* Legacy URL routes, redirect old paths to new service pages */}
        <Route path={"/home"} component={Home} />
        <Route path={"/ai-marketing-aeo-geo-blog"} component={Blog} />
        <Route path={"/ai-marketing-aeo-geo-blog/:rest*"} component={Blog} />
        <Route path={"/ai-seo-aeo-geo-aio-agency-london"} component={ServiceSEO} />
        <Route path={"/ppc-performance-max-agency-london"} component={ServicePPC} />
        <Route path={"/ai-marketing-strategy-genai-content-ops-london"} component={ServiceStrategy} />
        <Route path={"/free-ai-growth-audit"} component={AuditPage} />
        <Route path={"/free-ai-growth-audit/"} component={AuditPage} />
        <Route path={"/free-ai-growth-audit/thank-you"} component={FreeAuditThankYouPage} />
        <Route path={"/free-ai-growth-audit/thank-you/"} component={FreeAuditThankYouPage} />
        <Route path={"/admin/leads"} component={AdminLeads} />
        <Route path={"/admin/leads/"} component={AdminLeads} />
        <Route path={"/admin/contacts"} component={AdminContacts} />
        <Route path={"/admin/contacts/"} component={AdminContacts} />
        <Route path={"/admin/pagespeed"} component={PageSpeedDashboard} />
        <Route path={"/admin/pagespeed/"} component={PageSpeedDashboard} />
        <Route path={"/admin/broken-links"} component={AdminBrokenLinks} />
        <Route path={"/admin/broken-links/"} component={AdminBrokenLinks} />
        <Route path={"/admin/analytics"} component={AdminAnalytics} />
        <Route path={"/admin/analytics/"} component={AdminAnalytics} />
        <Route path={"/admin/linkartemis-imports"} component={AdminLinkArtemisImports} />
        <Route path={"/admin/linkartemis-imports/"} component={AdminLinkArtemisImports} />
        <Route path={"/admin/newsletter-subscribers"} component={AdminNewsletterSubscribers} />
        <Route path={"/admin/newsletter-subscribers/"} component={AdminNewsletterSubscribers} />
        <Route path={"/unsubscribed"} component={Unsubscribed} />
        <Route path={"/unsubscribed/"} component={Unsubscribed} />
        <Route path={"/modi-elnadi"} component={ModiElnadi} />
        <Route path={"/modi-elnadi/"} component={ModiElnadi} />
        <Route path={"/store"} component={Home} />
        <Route path={"/404"} component={NotFound} />
        {/* Final fallback route */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

/** Watches session expiry: shows a re-auth modal for admins, a toast for regular users. */
function SessionExpiryWatcher() {
  const { isAuthenticated, user } = useAuth();
  const role = user?.role ?? null;
  const { showModal, msUntilExpiry, dismissModal } = useSessionExpiry(isAuthenticated, role);
  return (
    <AdminReAuthModal
      open={showModal}
      msUntilExpiry={msUntilExpiry}
      onDismiss={dismissModal}
    />
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Redirects />
          <Toaster />
          <GlobalBreadcrumbs />
          <SessionExpiryWatcher />
          <Router />
          <SalesAssistantLoader />
          <CookieConsent />
          <ScoreBookingCompletionListener />
          <MobileScoreCta />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
